import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gamepad2, QrCode, Key, Loader2 } from "lucide-react";

export default function PasskeyLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [step, setStep] = useState("input");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [credential, setCredential] = useState(null);

  if (user) return <Navigate to="/" replace />;

  useEffect(() => {
    if (step === "ready" && credential) {
      verifyLogin();
    }
  }, [credential]);

  const startPasskeyLogin = async () => {
    setLoading(true);
    setError("");
    
    try {
      const res = await fetch("/api/passkey/login/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.detail || "Failed to start passkey login");
        setLoading(false);
        return;
      }

      const challenge = data.challenge;
      const publicKey = {
        challenge: new Uint8Array(
          atob(challenge.replace(/-/g, "+").replace(/_/g, "/"))
            .split("")
            .map(c => c.charCodeAt(0))
        ),
        rp: { id: window.location.hostname, name: "FullEcosystem" },
        timeout: 60000,
        userVerification: "preferred",
      };

      const cred = await navigator.credentials.get({ publicKey });
      
      const credData = {
        id: cred.id,
        type: cred.type,
        rawId: Array.from(new Uint8Array(cred.rawId)),
        response: {
          authenticatorData: Array.from(new Uint8Array(cred.response.authenticatorData)),
          clientDataJSON: Array.from(new Uint8Array(cred.response.clientDataJSON)),
          signature: Array.from(new Uint8Array(cred.response.signature)),
        },
      };
      
      setCredential({ ...credData, challenge, username });
      setStep("ready");
    } catch (e) {
      console.error(e);
      setError("Passkey authentication failed. Use passkey login instead.");
    }
    setLoading(false);
  };

  const verifyLogin = async () => {
    if (!credential) return;
    
    setLoading(true);
    try {
      const res = await fetch("/api/passkey/login/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: credential.username,
          credential: credential,
          challenge: credential.challenge,
        }),
      });
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.detail || "Verification failed");
        setStep("input");
        setLoading(false);
        return;
      }

      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
      }
      
      navigate("/");
    } catch (e) {
      setError("Verification failed");
      setStep("input");
    }
    setLoading(false);
  };

  const handlePasskey = async (e) => {
    e.preventDefault();
    if (!window.PublicKeyCredential) {
      setError("WebAuthn not supported in this browser");
      return;
    }
    await startPasskeyLogin();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="h-16 w-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Gamepad2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Passkey Login</h1>
          <p className="text-slate-400 mt-2">Use passkey or QR code</p>
        </div>

        <form onSubmit={handlePasskey} className="space-y-4">
          <div>
            <Label className="text-slate-300 text-sm">Username</Label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white mt-1"
              placeholder="Enter username"
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}

          <Button 
            type="submit" 
            disabled={loading || !username}
            className="w-full bg-emerald-500 hover:bg-emerald-600"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Key className="h-4 w-4 mr-2" />
                Sign in with Passkey
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 p-4 bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <QrCode className="h-4 w-4 text-slate-400" />
            <span className="text-slate-300 text-sm font-medium">QR Code Login</span>
          </div>
          <p className="text-slate-400 text-xs">
            Scan QR code with your mobile device to login using passkey.
          </p>
          <div className="mt-3 bg-white p-2 rounded-lg flex items-center justify-center">
            <div className="text-slate-400 text-center">
              <QrCode className="h-24 w-24 mx-auto opacity-50" />
              <p className="text-xs mt-2 text-slate-500">QR Code</p>
            </div>
          </div>
        </div>

        <Button 
          variant="ghost" 
          className="w-full mt-4 text-slate-400"
          onClick={() => navigate("/login")}
        >
          Use password login
        </Button>
      </div>
    </div>
  );
}